import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './DataLibraryModal.module.scss'
import { buildLibraryShareUrl } from '../../services/urlState'

function fallbackCopy(text, onDone) {
  const el = document.createElement('textarea')
  el.value = text
  el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
  document.body.appendChild(el)
  el.focus()
  el.select()
  try { document.execCommand('copy'); onDone?.() } catch (_) {}
  document.body.removeChild(el)
}

// ── Thumbnail SVG generators ──────────────────────────────────────────────────
const G = { d: '#1E9696', m: '#2CBDBD', l: '#7DD8D8', p: '#D0F0F0', grid: '#E5E7EB' }
const svgWrap = (inner) =>
  `<svg viewBox="0 0 140 80" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`

const THUMBS = {
  bar: () =>
    svgWrap([40, 12, 58, 30, 20].map((h, i) => `<rect x="${14 + i * 24}" y="${70 - h}" width="16" height="${h}" rx="2" fill="${i % 2 ? G.l : G.m}"/>`).join('')),
  line: () =>
    svgWrap(`<polyline points="8,60 34,44 60,50 88,22 132,12" fill="none" stroke="${G.m}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` +
      [8,60,34,44,60,50,88,22,132,12].reduce((a,_,i,A) => i%2 ? a : a + `<circle cx="${A[i]}" cy="${A[i+1]}" r="3" fill="${G.d}"/>`, '')),
  multibar: () =>
    svgWrap([[44,30],[26,52],[58,38],[34,46]].map((g,i) => `<rect x="${12+i*32}" y="${70-g[0]}" width="12" height="${g[0]}" rx="2" fill="${G.m}"/><rect x="${24+i*32}" y="${70-g[1]}" width="12" height="${g[1]}" rx="2" fill="${G.p}"/>`).join('')),
  slope: () =>
    svgWrap(`<line x1="30" y1="20" x2="110" y2="56" stroke="${G.m}" stroke-width="2.5"/><line x1="30" y1="46" x2="110" y2="28" stroke="${G.l}" stroke-width="2.5"/><line x1="30" y1="60" x2="110" y2="44" stroke="${G.d}" stroke-width="2.5"/>` +
      [20,46,60].map(y => `<circle cx="30" cy="${y}" r="3.5" fill="${G.d}"/>`).join('') +
      [56,28,44].map(y => `<circle cx="110" cy="${y}" r="3.5" fill="${G.d}"/>`).join('')),
  bump: () =>
    svgWrap(`<path d="M14,30 C44,30 44,18 74,18 S104,40 126,40" fill="none" stroke="${G.m}" stroke-width="2.5"/><path d="M14,50 C44,50 44,56 74,56 S104,22 126,22" fill="none" stroke="${G.l}" stroke-width="2.5"/><path d="M14,18 C44,18 44,42 74,42 S104,56 126,56" fill="none" stroke="${G.d}" stroke-width="2.5"/>`),
  pie: () =>
    svgWrap(`<circle cx="70" cy="40" r="30" fill="${G.p}"/><path d="M70,40 L70,10 A30,30 0 0,1 96,55 Z" fill="${G.m}"/><path d="M70,40 L96,55 A30,30 0 0,1 48,60 Z" fill="${G.l}"/><path d="M70,40 L48,60 A30,30 0 0,1 49,21 Z" fill="${G.d}"/>`),
  bubble: () =>
    svgWrap(`<circle cx="42" cy="44" r="20" fill="${G.l}" opacity=".85"/><circle cx="86" cy="32" r="13" fill="${G.m}" opacity=".85"/><circle cx="104" cy="54" r="9" fill="${G.d}" opacity=".85"/><circle cx="66" cy="22" r="7" fill="${G.m}" opacity=".85"/><circle cx="24" cy="22" r="6" fill="${G.p}"/>`),
  pointmap: () =>
    svgWrap(`<rect x="6" y="8" width="128" height="64" rx="6" fill="#f0f4f1"/>` +
      [[28,24],[52,40,3],[78,18],[96,46,4],[60,56],[112,30],[40,58],[88,62],[120,52]].map(([x,y,r=2.5]) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${G.m}" opacity=".8"/>`).join('')),
  hexmap: () => {
    let h = ''
    const hx = (cx, cy, r, f) => {
      const pts = []
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 180) * (60 * i - 30)
        pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`)
      }
      return `<polygon points="${pts.join(' ')}" fill="${f}"/>`
    }
    ;[[44,30,G.p],[62,30,G.m],[80,30,G.l],[53,46,G.m],[71,46,G.d],[35,46,G.l],[62,62,G.p],[80,62,G.m]].forEach(([cx,cy,f]) => { h += hx(cx,cy,9,f) })
    return svgWrap(h)
  },
  voronoi: () =>
    svgWrap(`<rect x="6" y="8" width="128" height="64" rx="6" fill="#f5f8f6"/><path d="M6,40 L46,8 M46,8 L92,30 M92,30 L134,18 M46,8 L40,72 M92,30 L88,72 M92,30 L134,52 M40,40 L6,52" stroke="${G.l}" stroke-width="1.5" fill="none" opacity=".8"/>` +
      [[34,30],[70,40],[108,34],[60,60],[24,52]].map(([x,y]) => `<circle cx="${x}" cy="${y}" r="2.5" fill="${G.d}"/>`).join('')),
  contour: () =>
    svgWrap(`<rect x="6" y="8" width="128" height="64" rx="6" fill="#f3f7f4"/><ellipse cx="70" cy="40" rx="46" ry="26" fill="none" stroke="${G.p}" stroke-width="2"/><ellipse cx="70" cy="40" rx="32" ry="18" fill="none" stroke="${G.l}" stroke-width="2"/><ellipse cx="70" cy="40" rx="18" ry="10" fill="none" stroke="${G.m}" stroke-width="2"/><ellipse cx="70" cy="40" rx="6" ry="4" fill="${G.d}"/>`),
  sankey: () =>
    svgWrap(`<rect x="8" y="14" width="9" height="22" rx="1.5" fill="${G.d}"/><rect x="8" y="44" width="9" height="18" rx="1.5" fill="${G.m}"/><rect x="123" y="10" width="9" height="18" rx="1.5" fill="${G.d}"/><rect x="123" y="34" width="9" height="16" rx="1.5" fill="${G.m}"/><rect x="123" y="56" width="9" height="12" rx="1.5" fill="${G.l}"/><path d="M17,22 C70,22 70,16 123,16" stroke="${G.l}" stroke-width="9" fill="none" opacity=".55"/><path d="M17,30 C70,30 70,40 123,40" stroke="${G.m}" stroke-width="7" fill="none" opacity=".45"/><path d="M17,54 C70,54 70,60 123,60" stroke="${G.p}" stroke-width="8" fill="none" opacity=".7"/>`),
  chord: () =>
    svgWrap(`<circle cx="70" cy="40" r="30" fill="none" stroke="${G.p}" stroke-width="5"/><path d="M44,26 Q70,40 96,30" stroke="${G.m}" stroke-width="2.5" fill="none"/><path d="M50,62 Q70,40 92,55" stroke="${G.d}" stroke-width="2.5" fill="none"/><path d="M42,48 Q70,40 88,18" stroke="${G.l}" stroke-width="2.5" fill="none"/>`),
  alluvial: () =>
    svgWrap(`<rect x="10" y="12" width="8" height="20" fill="${G.d}"/><rect x="10" y="40" width="8" height="26" fill="${G.m}"/><rect x="122" y="14" width="8" height="24" fill="${G.m}"/><rect x="122" y="46" width="8" height="20" fill="${G.l}"/><path d="M18,22 C70,22 70,26 122,26" stroke="${G.m}" stroke-width="11" fill="none" opacity=".4"/><path d="M18,52 C70,52 70,56 122,56" stroke="${G.l}" stroke-width="11" fill="none" opacity=".5"/><path d="M18,30 C70,30 70,52 122,52" stroke="${G.p}" stroke-width="6" fill="none" opacity=".6"/>`),
  arc: () =>
    svgWrap(`<line x1="10" y1="58" x2="130" y2="58" stroke="${G.grid}" stroke-width="1.5"/>` +
      [[24,52],[24,90],[42,80],[58,108]].map(([x,w]) => `<path d="M${x},58 A${w/2},${w/2} 0 0 1 ${x+w},58" fill="none" stroke="${G.m}" stroke-width="2"/>`).join('') +
      [24,48,66,90,108,132].map(x => `<circle cx="${x}" cy="58" r="3" fill="${G.d}"/>`).join('')),
  sunburst: () =>
    svgWrap(`<circle cx="70" cy="40" r="11" fill="${G.d}"/><path d="M70,40 L70,18 A22,22 0 0,1 89,29 Z" fill="${G.m}"/><path d="M70,40 L89,29 A22,22 0 0,1 85,55 Z" fill="${G.l}"/><path d="M70,40 L85,55 A22,22 0 0,1 55,55 Z" fill="${G.p}"/><path d="M70,40 L55,55 A22,22 0 0,1 70,18 Z" fill="${G.m}" opacity=".7"/><path d="M70,18 A30,30 0 0,1 97,33 L89,29 A22,22 0 0,0 70,18Z" fill="${G.l}"/><path d="M89,55 A30,30 0 0,1 60,69 L62,55 A22,22 0 0,0 85,55Z" fill="${G.p}"/>`),
  dendrogram: () =>
    svgWrap(`<path d="M14,20 H40 M14,32 H40 M40,20 V32 M40,26 H66 M14,52 H50 M14,64 H50 M50,52 V64 M50,58 H66 M66,26 V58 M66,42 H110" fill="none" stroke="${G.m}" stroke-width="2"/>` +
      [[14,20],[14,32],[14,52],[14,64]].map(([x,y]) => `<circle cx="${x}" cy="${y}" r="3" fill="${G.d}"/>`).join('')),
  treemap: () =>
    svgWrap(`<rect x="6" y="8" width="64" height="64" rx="2" fill="${G.m}"/><rect x="73" y="8" width="40" height="36" rx="2" fill="${G.l}"/><rect x="116" y="8" width="18" height="36" rx="2" fill="${G.p}"/><rect x="73" y="47" width="61" height="25" rx="2" fill="${G.d}"/>`),
  packing: () =>
    svgWrap(`<circle cx="70" cy="40" r="34" fill="none" stroke="${G.p}" stroke-width="1.5"/><circle cx="56" cy="34" r="16" fill="${G.l}"/><circle cx="86" cy="46" r="13" fill="${G.m}"/><circle cx="82" cy="24" r="8" fill="${G.d}"/><circle cx="56" cy="58" r="7" fill="${G.m}"/>`),
  radar: () =>
    svgWrap(`<polygon points="70,10 106,30 96,66 44,66 34,30" fill="none" stroke="${G.grid}" stroke-width="1.5"/><polygon points="70,20 96,34 86,58 52,56 46,34" fill="${G.l}" opacity=".4" stroke="${G.m}" stroke-width="2.5"/>`),
  parallel: () =>
    svgWrap([20,52,84,116].map(x => `<line x1="${x}" y1="10" x2="${x}" y2="70" stroke="${G.grid}" stroke-width="1.5"/>`).join('') +
      `<polyline points="20,24 52,50 84,20 116,44" fill="none" stroke="${G.m}" stroke-width="2"/><polyline points="20,52 52,28 84,56 116,22" fill="none" stroke="${G.l}" stroke-width="2"/><polyline points="20,40 52,62 84,36 116,60" fill="none" stroke="${G.d}" stroke-width="2"/>`),
  beeswarm: () =>
    svgWrap(`<line x1="14" y1="40" x2="126" y2="40" stroke="${G.grid}" stroke-width="1.5"/>` +
      [[30,40],[44,32],[44,48],[58,40],[58,28],[58,52],[72,36],[72,44],[86,40],[86,30],[100,40],[114,38]].map(([x,y]) => `<circle cx="${x}" cy="${y}" r="4" fill="${G.m}" opacity=".8"/>`).join('')),
  violin: () =>
    svgWrap(`<path d="M40,12 C24,28 24,52 40,68 C56,52 56,28 40,12Z" fill="${G.p}" stroke="${G.m}" stroke-width="1.5"/><line x1="40" y1="20" x2="40" y2="60" stroke="${G.d}" stroke-width="2"/><path d="M96,16 C84,30 84,50 96,64 C108,50 108,30 96,16Z" fill="${G.l}" stroke="${G.m}" stroke-width="1.5"/><line x1="96" y1="24" x2="96" y2="56" stroke="${G.d}" stroke-width="2"/>`),
  heatmap: () => {
    const c = [G.p, G.l, G.m, G.d]
    let h = ''
    for (let r = 0; r < 4; r++) for (let k = 0; k < 6; k++) h += `<rect x="${20+k*16}" y="${14+r*14}" width="14" height="12" rx="1.5" fill="${c[(r+k)%4]}"/>`
    return svgWrap(h)
  },
  horizon: () =>
    svgWrap(`<path d="M8,70 L8,52 Q40,30 72,44 T132,38 L132,70Z" fill="${G.p}"/><path d="M8,70 L8,60 Q40,46 72,56 T132,52 L132,70Z" fill="${G.l}"/><path d="M8,70 L8,66 Q40,58 72,64 T132,62 L132,70Z" fill="${G.m}"/>`),
  streamgraph: () =>
    svgWrap(`<path d="M8,40 C40,20 70,24 132,36 C70,30 40,30 8,46Z" fill="${G.d}"/><path d="M8,46 C40,30 70,30 132,36 C70,42 40,44 8,54Z" fill="${G.m}"/><path d="M8,54 C40,44 70,42 132,36 C70,52 40,56 8,60Z" fill="${G.l}"/>`),
  gantt: () =>
    svgWrap([[14,10,60],[40,24,40],[24,38,80],[56,52,50]].map(([x,y,w]) => `<rect x="${x}" y="${y}" width="${w}" height="10" rx="3" fill="${G.m}"/>`).join('') +
      `<line x1="8" y1="6" x2="8" y2="68" stroke="${G.grid}" stroke-width="1.5"/>`),
}

// ── Category definitions ──────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',  label: 'All datasets',    icon: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>' },
  { id: 'time', label: 'Time series',     icon: '<path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/>' },
  { id: 'geo',  label: 'Geographic',      icon: '<path d="M12 21s-7-6.3-7-11a7 7 0 0114 0c0 4.7-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>' },
  { id: 'rank', label: 'Ranking',         icon: '<rect x="4" y="10" width="4" height="10"/><rect x="10" y="5" width="4" height="15"/><rect x="16" y="13" width="4" height="7"/>' },
  { id: 'hier', label: 'Hierarchy',       icon: '<rect x="9" y="3" width="6" height="5" rx="1"/><rect x="3" y="16" width="6" height="5" rx="1"/><rect x="15" y="16" width="6" height="5" rx="1"/><path d="M12 8v4M6 16v-2h12v2"/>' },
  { id: 'flow', label: 'Flow & network',  icon: '<circle cx="5" cy="6" r="2.5"/><circle cx="19" cy="6" r="2.5"/><circle cx="12" cy="18" r="2.5"/><path d="M7 7l4 9M17 7l-4 9"/>' },
  { id: 'dist', label: 'Distribution',    icon: '<path d="M3 20c4 0 4-14 9-14s5 14 9 14"/>' },
]

// ── Dataset catalog ───────────────────────────────────────────────────────────
const BASE = process.env.PUBLIC_URL || ''
const CATALOG = [
  { id: 'nba',         title: '2026 NBA Finals Game 5 — Knicks vs Spurs shot chart',        chartTypes: ['Hexbin map'],                          src: 'NBA Stats',                  rows: 480,   categories: ['geo'],          thumb: 'hexmap',      url: `${BASE}/sample-datasets/Hexbin - 2026 Knicks v Spurs Finals shots.tsv`,           delimiter: '\t' },
  { id: 'wine',        title: 'Aromas of wine and frequency',                                 chartTypes: ['Sunburst'],                            src: 'Own work',                   rows: 96,    categories: ['hier'],         thumb: 'sunburst',    url: `${BASE}/sample-datasets/Sunburst - Wine Aromas.tsv`,                               delimiter: '\t' },
  { id: 'energy',      title: 'Energy flows in UK (2050)',                                    chartTypes: ['Sankey'],                              src: 'gov.uk',                     rows: 48,    categories: ['flow'],         thumb: 'sankey',      url: `${BASE}/sample-datasets/Sankey diagram - Energy flows.tsv`,                        delimiter: '\t' },
  { id: 'migration',   title: 'Estimated migration flows between macro-areas, 1960–1970',     chartTypes: ['Chord diagram'],                       src: 'Gui J. Abel · Data to Viz',  rows: 64,    categories: ['flow'],         thumb: 'chord',       url: `${BASE}/sample-datasets/Chord Diagram - Estimated migration flows between macro-areas.csv`, delimiter: ',' },
  { id: 'eu-prices',   title: 'EU index of consumer prices',                                  chartTypes: ['Horizon graph'],                       src: 'Eurostat',                   rows: 1248,  categories: ['time'],         thumb: 'horizon',     url: `${BASE}/sample-datasets/Horizon Graph - EU Index of consumer prices.tsv`,          delimiter: '\t' },
  { id: 'felidae',     title: 'Felidae classification (cats and friends)',                     chartTypes: ['Dendrogram', 'Circular dendrogram'],   src: 'Special thanks to interns',  rows: 140,   categories: ['hier'],         thumb: 'dendrogram',  url: `${BASE}/sample-datasets/Dendrogram - Felidae classification.tsv`,                  delimiter: '\t' },
  { id: 'fifa',        title: 'FIFA players statistics',                                       chartTypes: ['Radar chart'],                         src: 'K. Gadiya · Kaggle',         rows: 18207, categories: ['dist'],         thumb: 'radar',       url: `${BASE}/sample-datasets/Radar Chart - Fifa players.tsv`,                           delimiter: '\t' },
  { id: 'milan',       title: 'Foreign residents in Milan',                                    chartTypes: ['Bump chart'],                          src: 'Comune di Milano',           rows: 210,   categories: ['time', 'rank'], thumb: 'bump',        url: `${BASE}/sample-datasets/Bump chart - Foreign residents in Milan.tsv`,              delimiter: '\t' },
  { id: 'gdp',         title: 'GDP sector composition',                                        chartTypes: ['Pie chart', 'Stacked bar'],            src: 'Wikipedia',                  rows: 200,   categories: ['rank'],         thumb: 'pie',         url: `${BASE}/sample-datasets/Stacked barchart - GDP sector composition.tsv`,            delimiter: '\t' },
  { id: 'earthquakes', title: 'Global earthquakes from 2015 to 2024',                         chartTypes: ['Point map'],                           src: 'Sample dataset',             rows: 8430,  categories: ['geo'],          thumb: 'pointmap',    url: `${BASE}/sample-datasets/Point Map - Global Earthquakes from 2015 to 2024.csv`,     delimiter: ',' },
  { id: 'happiness',   title: 'Happiness index',                                               chartTypes: ['Multiset bar'],                        src: 'World Happiness Report',     rows: 156,   categories: ['rank'],         thumb: 'multibar',    url: `${BASE}/sample-datasets/Multiset Barchart - Happiness Index.tsv`,                  delimiter: '\t' },
  { id: 'hate-crimes', title: 'Hate crimes in New York',                                      chartTypes: ['Alluvial diagram'],                    src: 'NYC Open Data',              rows: 520,   categories: ['flow'],         thumb: 'alluvial',    url: `${BASE}/sample-datasets/Alluvial diagram - Hate crimes in New York.tsv`,           delimiter: '\t' },
  { id: 'movies',      title: 'Highest grossing movies in history',                            chartTypes: ['Bubble chart'],                        src: 'Wikipedia',                  rows: 50,    categories: ['rank'],         thumb: 'bubble',      url: `${BASE}/sample-datasets/Bubble Chart - TOP 50 Groossing Movies.tsv`,               delimiter: '\t' },
  { id: 'iris',        title: 'Iris flowers',                                                  chartTypes: ['Convex hull', 'Parallel coordinates'], src: 'R. Fisher',                  rows: 150,   categories: ['dist'],         thumb: 'parallel',    url: `${BASE}/sample-datasets/Convex hull - Iris flowers.tsv`,                           delimiter: '\t' },
  { id: 'italy',       title: 'Italian PMs and Presidents',                                    chartTypes: ['Gantt chart'],                         src: 'Wikidata',                   rows: 120,   categories: ['time'],         thumb: 'gantt',       url: `${BASE}/sample-datasets/Gantt chart - Italian PMs and presidents.tsv`,             delimiter: '\t' },
  { id: 'kobe',        title: 'Kobe Bryant shoots',                                            chartTypes: ['Hexagonal binning', 'Voronoi'],        src: 'Kaggle',                     rows: 1560,  categories: ['geo'],          thumb: 'voronoi',     url: `${BASE}/sample-datasets/Hexbin - basketball shots.tsv`,                            delimiter: '\t' },
  { id: 'got',         title: 'Lannister vs Stark relationships',                              chartTypes: ['Arc diagram'],                         src: 'T. Breid · Kaggle',          rows: 96,    categories: ['flow'],         thumb: 'arc',         url: `${BASE}/sample-datasets/Arc diagrams - Lannister vs Starck relationships.tsv`,     delimiter: '\t' },
  { id: 'letters',     title: 'Letter frequency by language',                                  chartTypes: ['Matrix plot (heatmap)'],               src: 'Wikipedia',                  rows: 130,   categories: ['dist'],         thumb: 'heatmap',     url: `${BASE}/sample-datasets/Matrix Plot - Letters frequencies by language.tsv`,        delimiter: '\t' },
  { id: 'meteorites',  title: 'Meteorite landings',                                            chartTypes: ['Point map'],                           src: 'NASA Open Data',             rows: 20000, categories: ['geo'],          thumb: 'pointmap',    url: `${BASE}/sample-datasets/Metoerite Landings (small).csv`,                           delimiter: ',',  large: true },
  { id: 'cities',      title: 'Most populated cities per continent',                           chartTypes: ['Circle packing'],                      src: 'Wikidata',                   rows: 300,   categories: ['hier'],         thumb: 'packing',     url: `${BASE}/sample-datasets/Circle Packing - Most populated cities.tsv`,              delimiter: '\t' },
  { id: 'netflix',     title: 'Netflix Original Series 2013/2017',                             chartTypes: ['Bar chart'],                           src: 'M. Schroyer · Data World',   rows: 160,   categories: ['rank'],         thumb: 'bar',         url: `${BASE}/sample-datasets/Bar chart - Netflix Original Series.tsv`,                  delimiter: '\t' },
  { id: 'ny-wages',    title: 'New York mean wage per occupation',                             chartTypes: ['Beeswarm plot'],                       src: 'NYC Open Data',              rows: 780,   categories: ['dist'],         thumb: 'beeswarm',    url: `${BASE}/sample-datasets/Beeswarm plot - NY mean wages.tsv`,                        delimiter: '\t' },
  { id: 'olympics',    title: 'Olympics medals',                                               chartTypes: ['Streamgraph'],                         src: 'D. Agrawal · Kaggle',        rows: 2400,  categories: ['time'],         thumb: 'streamgraph', url: `${BASE}/sample-datasets/Streamgraph - Olympics Medals.tsv`,                        delimiter: '\t' },
  { id: 'orchestra',   title: 'Orchestras by musical instrument',                              chartTypes: ['Treemap'],                             src: 'Wikipedia',                  rows: 120,   categories: ['hier'],         thumb: 'treemap',     url: `${BASE}/sample-datasets/Treemap - Orchestra.tsv`,                                  delimiter: '\t' },
  { id: 'football',    title: 'Premier League and Serie A points',                             chartTypes: ['Slope chart'],                         src: 'Wikipedia',                  rows: 40,    categories: ['rank', 'time'], thumb: 'slope',       url: `${BASE}/sample-datasets/Slope graph - Premier SerieA ranking.tsv`,                delimiter: '\t' },
  { id: 'riaa',        title: 'Revenues per music format',                                     chartTypes: ['Line chart'],                          src: 'RIAA',                       rows: 660,   categories: ['time'],         thumb: 'line',        url: `${BASE}/sample-datasets/Line chart - RIAA Music format revenues.tsv`,              delimiter: '\t' },
  { id: 'paris-temp',  title: 'Temperature in Paris during World War II',                      chartTypes: ['Contour plot'],                        src: 'NOAA',                       rows: 2100,  categories: ['time', 'geo'],  thumb: 'contour',     url: `${BASE}/sample-datasets/Contour plot - Mean temperature in Paris 1944-1945.tsv`,  delimiter: '\t' },
  { id: 'ny-weather',  title: 'Weather in New York, 2012–2015',                                chartTypes: ['Violin plot', 'Boxplot'],              src: 'NOAA · Vega Dataset',        rows: 1460,  categories: ['dist'],         thumb: 'violin',      url: `${BASE}/sample-datasets/Violin plot - Weather in New York.tsv`,                    delimiter: '\t' },
]

function fmtRows(n) {
  return n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k rows` : `${n} rows`
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DataLibraryModal({ open, onClose, onLoad, onHome }) {
  const [activeCat, setActiveCat] = useState('all')
  const [query, setQuery]         = useState('')
  const [sortMode, setSortMode]   = useState('az')
  const [largeOnly, setLargeOnly] = useState(false)
  const [selected, setSelected]   = useState(null)
  const [loading, setLoading]     = useState(false)
  const [copied, setCopied]       = useState(false)

  // Reset state each time the modal opens
  useEffect(() => {
    if (open) {
      setActiveCat('all')
      setQuery('')
      setSortMode('az')
      setLargeOnly(false)
      setSelected(null)
      setLoading(false)
    }
  }, [open])

  // Escape key
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Body scroll lock
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [open])

  const filtered = useMemo(() => {
    let list = CATALOG.filter((d) => {
      if (activeCat !== 'all' && !d.categories.includes(activeCat)) return false
      if (largeOnly && !d.large) return false
      if (query) {
        const hay = `${d.title} ${d.src} ${d.chartTypes.join(' ')}`.toLowerCase()
        if (!hay.includes(query.toLowerCase())) return false
      }
      return true
    })
    list = [...list].sort((a, b) => {
      if (sortMode === 'az')   return a.title.localeCompare(b.title)
      if (sortMode === 'za')   return b.title.localeCompare(a.title)
      if (sortMode === 'rows') return b.rows - a.rows
      if (sortMode === 'type') return a.chartTypes[0].localeCompare(b.chartTypes[0])
      return 0
    })
    return list
  }, [activeCat, query, sortMode, largeOnly])

  const handleLoad = useCallback(async () => {
    if (!selected || loading) return
    setLoading(true)
    try {
      const response = await fetch(selected.url)
      if (!response.ok) throw new Error(`Failed to fetch ${selected.title}`)
      const text = await response.text()
      const thumbHtml = THUMBS[selected.thumb] ? THUMBS[selected.thumb]() : ''
      onLoad(text, selected.delimiter, { ...selected, thumbHtml })
    } catch (e) {
      window.console.error(e)
      setLoading(false)
    }
  }, [selected, loading, onLoad])

  const toggleLarge = useCallback(() => setLargeOnly((v) => !v), [])

  const selectCard = useCallback((dataset) => {
    setSelected((prev) => (prev?.id === dataset.id ? null : dataset))
  }, [])

  const clearFilters = useCallback(() => {
    setActiveCat('all')
    setQuery('')
    setLargeOnly(false)
  }, [])

  if (!open) return null

  const catCount = (catId) =>
    CATALOG.filter((d) => (catId === 'all' || d.categories.includes(catId)) && (!largeOnly || d.large)).length

  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Sample data library">

      {/* Top bar */}
      <div className={styles.topbar}>
        {/* Same contract as the app header's brand: a real href so it can be
            opened in a new tab, with the in-app navigation preferred when a
            handler is supplied. */}
        <a
          className={styles.brand}
          href="/"
          onClick={onHome ? (e) => { e.preventDefault(); onHome() } : undefined}
        >
          lug<span>sail</span>
        </a>
        <div className={styles.search}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
          </svg>
          <input
            type="text"
            placeholder="Search datasets, sources, chart types…"
            aria-label="Search datasets"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button
          className={styles.shareBtn}
          onClick={() => {
            const url = buildLibraryShareUrl()
            const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000) }
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(url).then(done).catch(() => fallbackCopy(url, done))
            } else {
              fallbackCopy(url, done)
            }
          }}
          aria-label="Copy share link"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          <span>{copied ? 'Copied!' : 'Share'}</span>
        </button>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close library">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className={styles.body}>

        {/* Left rail */}
        <aside className={styles.rail}>
          <p className={styles.railHeading}>Categories</p>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.catBtn} ${activeCat === cat.id ? styles.catActive : ''}`}
              onClick={() => setActiveCat(cat.id)}
            >
              <span className={styles.catLabel}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                  dangerouslySetInnerHTML={{ __html: cat.icon }} />
                {cat.label}
              </span>
              <span className={styles.catCount}>{catCount(cat.id)}</span>
            </button>
          ))}

          <div className={styles.divide} />

          <div
            className={`${styles.toggle} ${largeOnly ? styles.toggleActive : ''}`}
            role="switch"
            aria-checked={largeOnly}
            tabIndex={0}
            onClick={toggleLarge}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLarge() } }}
          >
            <span className={styles.switchEl} />
            <span>Large datasets only</span>
          </div>
        </aside>

        {/* Main */}
        <div className={styles.main}>
          {/* Mobile-only category chips */}
          <div className={styles.mobileCatBar}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`${styles.mobileCatChip} ${activeCat === cat.id ? styles.mobileCatChipActive : ''}`}
                onClick={() => setActiveCat(cat.id)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  dangerouslySetInnerHTML={{ __html: cat.icon }}
                />
                {cat.label}
              </button>
            ))}
          </div>

          <div className={styles.mainHead}>
            <div className={styles.resultCount}>
              <span className={styles.sectionLabel}>Sample library</span>
              <b>{filtered.length}</b> {filtered.length === 1 ? 'dataset' : 'datasets'}
            </div>
            <div className={styles.sortRow}>
              <label htmlFor="dlm-sort">Sort</label>
              <select
                id="dlm-sort"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
              >
                <option value="az">Name A–Z</option>
                <option value="za">Name Z–A</option>
                <option value="rows">Rows (high → low)</option>
                <option value="type">Chart type</option>
              </select>
            </div>
          </div>

          <div className={styles.scroll}>
            {filtered.length === 0 ? (
              <div className={styles.empty}>
                <svg className={styles.emptyIcon} width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
                </svg>
                <p>No datasets match your search.</p>
                <button className={styles.clearLink} onClick={clearFilters}>Clear filters</button>
              </div>
            ) : (
              <div className={styles.grid}>
                {filtered.map((d) => {
                  const isSelected = selected?.id === d.id
                  const thumbHtml = THUMBS[d.thumb] ? THUMBS[d.thumb]() : ''
                  return (
                    <div
                      key={d.id}
                      className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
                      tabIndex={0}
                      role="button"
                      aria-label={d.title}
                      aria-pressed={isSelected}
                      onClick={() => selectCard(d)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCard(d) } }}
                    >
                      {isSelected && (
                        <span className={styles.checkmark}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M5 12l5 5L20 6"/>
                          </svg>
                        </span>
                      )}
                      <div className={styles.thumb} dangerouslySetInnerHTML={{ __html: thumbHtml }} />
                      <div className={styles.cardBody}>
                        <div className={styles.cardTitle}>{d.title}</div>
                        <div className={styles.badges}>
                          {d.chartTypes.map((ct) => (
                            <span key={ct} className={styles.badge}>{ct}</span>
                          ))}
                          {d.large && <span className={`${styles.badge} ${styles.badgeLarge}`}>Large · {fmtRows(d.rows)}</span>}
                        </div>
                        <div className={styles.cardMeta}>
                          <span className={styles.cardSrc}>{d.src}</span>
                          <span>{d.large ? '' : fmtRows(d.rows)}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selection footer */}
      <div className={`${styles.footer} ${selected ? styles.footerVisible : ''}`}>
        <div className={styles.footerSummary}>
          Selected <b>{selected?.title ?? '—'}</b>
        </div>
        <button className={styles.ghostBtn} onClick={() => setSelected(null)}>Cancel</button>
        <button
          className={styles.primaryBtn}
          onClick={handleLoad}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Load this dataset'}
        </button>
      </div>
    </div>,
    document.body
  )
}
